/* ============================================================
   NOVA APPAREL COLOR PALETTES
   Canonical owner for garment style identity and display colors.

   Next Level references are manufacturer-published digital values from:
   https://catalog.nextlevelapparel.com/color-guide-2026

   They are display references, not a claim that a screen can reproduce
   dyed fabric or an official production color specification exactly.
   ============================================================ */
(function () {
  'use strict';

  if (window.NovaApparelColorAPI) return;

  var NEXT_LEVEL_COLOR_SOURCE =
    'Next Level Apparel 2026 Color Card with PMS, TCX, and HEX';
  var NEXT_LEVEL_COLOR_SOURCE_URL =
    'https://catalog.nextlevelapparel.com/color-guide-2026';

  var NORMALIZED_COLORS = {
    white: { hex: '#F4F9FF', source: NEXT_LEVEL_COLOR_SOURCE },
    black: { hex: '#25282A', source: NEXT_LEVEL_COLOR_SOURCE },
    red: { hex: '#CD212A', source: NEXT_LEVEL_COLOR_SOURCE },
    royal: { hex: '#1B5091', source: NEXT_LEVEL_COLOR_SOURCE },
    'midnight-navy': { hex: '#2A3244', source: NEXT_LEVEL_COLOR_SOURCE }
  };

  var STYLE_PALETTES = {};
  var STYLE_ORDER = [];

  function clean(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function key(value) {
    return clean(value).toLowerCase();
  }

  function skuKey(value) {
    return clean(value).toUpperCase().replace(/[^A-Z0-9]/g, '');
  }

  function copy(value) {
    return JSON.parse(JSON.stringify(value));
  }

  function normalizeColorRecord(colorName, definition, palette) {
    var record = typeof definition === 'string'
      ? { hex: definition }
      : copy(definition || {});
    var normalized = record.normalized && NORMALIZED_COLORS[record.normalized];

    if (normalized) {
      record.hex = normalized.hex;
      record.source = record.source || normalized.source;
    }

    return {
      colorName: colorName,
      canonicalKey: key(colorName).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
      hex: clean(record.hex).toUpperCase(),
      garmentSource: clean(record.garmentSource || palette.garmentSource || 'standard-unisex-crew'),
      aliases: Array.isArray(record.aliases) ? record.aliases.map(clean).filter(Boolean) : [],
      source: clean(record.source || palette.source),
      sourceUrl: clean(record.sourceUrl || palette.sourceUrl),
      classification: clean(
        record.classification || palette.classification || 'digital-display-representation'
      ),
      normalized: clean(record.normalized)
    };
  }

  function registerStylePalette(definition) {
    var palette = copy(definition || {});
    var id = key(palette.id);
    if (!id) throw new Error('Nova apparel palette requires a style id.');
    if (!palette.colors || typeof palette.colors !== 'object') {
      throw new Error('Nova apparel palette requires color data.');
    }

    palette.id = id;
    palette.productIds = (palette.productIds || []).map(clean);
    palette.handles = (palette.handles || []).map(key);
    palette.skuFamilies = (palette.skuFamilies || []).map(skuKey);
    palette.colorOrder = Object.keys(palette.colors);
    palette.colorLookup = {};

    palette.colorOrder.forEach(function (colorName) {
      var record = normalizeColorRecord(colorName, palette.colors[colorName], palette);
      if (!/^#[0-9A-F]{6}$/.test(record.hex)) {
        throw new Error('Nova apparel palette has an invalid display color: ' + colorName);
      }
      palette.colorLookup[key(colorName)] = record;
      record.aliases.forEach(function (alias) {
        var aliasKey = key(alias);
        if (!aliasKey || palette.colorLookup[aliasKey]) {
          throw new Error('Nova apparel palette has an invalid or duplicate color alias: ' + alias);
        }
        palette.colorLookup[aliasKey] = record;
      });
    });

    STYLE_PALETTES[id] = palette;
    if (STYLE_ORDER.indexOf(id) === -1) STYLE_ORDER.push(id);
    return id;
  }

  registerStylePalette({
    id: 'legacy-triblend-crew',
    manufacturer: 'Next Level',
    style: 'Legacy Triblend Golden Product',
    garmentFamily: 'standard-unisex-crew',
    garmentSource: 'nova-unisex-crew-{view}.svg',
    productIds: ['1763556327514'],
    handles: ['triblend-crew-by-next-level'],
    source: 'Existing approved Nova Triblend runtime values',
    classification: 'existing-approved-runtime-value',
    colors: {
      'Heather White': '#F2F1ED',
      'Premium Heather': '#A7A7A4',
      'Vintage Black': '#262626',
      'Vintage Red': '#9E343B',
      'Envy Triblend': '#278055',
      'Military Green Triblend': '#5C624B',
      'Vintage Turquoise': '#36AEB7',
      'Vintage Royal': '#315D9C',
      'Vintage Navy': '#263A55',
      'Indigo Triblend': '#3F5F73',
      'Vintage Purple': '#65486F',
      'Venetian Grey': '#77736F'
    }
  });

  registerStylePalette({
    id: 'next-level-3600',
    manufacturer: 'Next Level',
    style: '3600',
    garmentFamily: 'standard-unisex-crew',
    garmentSource: 'nova-unisex-crew-{view}.svg',
    productIds: ['8733623484669'],
    handles: ['next-level-cotton-fitted-crew'],
    skuFamilies: ['NL3600'],
    source: NEXT_LEVEL_COLOR_SOURCE,
    sourceUrl: NEXT_LEVEL_COLOR_SOURCE_URL,
    classification: 'manufacturer-published-digital-reference',
    colors: {
      White: { normalized: 'white' },
      Heather: {
        hex: '#BEBDBD',
        source: NEXT_LEVEL_COLOR_SOURCE + ' — Shopify Heather maps to Heather Gray'
      },
      'Heavy Metal': '#525A5B',
      Black: { normalized: 'black' },
      'Light Pink': '#F5BEC7',
      Red: { normalized: 'red' },
      Cardinal: '#8E3841',
      'Banana Cream': '#FBD897',
      Kelly: {
        hex: '#008C45',
        source: NEXT_LEVEL_COLOR_SOURCE + ' — Shopify Kelly maps to Kelly Green'
      },
      'Military Green': '#656344',
      'Light Olive': '#80856D',
      Teal: '#009999',
      'Tahiti Blue': '#33BECC',
      Turquoise: '#0088B0',
      'Light Blue': '#B5CED4',
      Royal: { normalized: 'royal' },
      'Midnight Navy': { normalized: 'midnight-navy' },
      'Purple Rush': '#603F83'
    },
    notes: [
      'Military Green and Midnight Navy share storefront media on the audited Shopify product.',
      'The duplicate media is not used as color evidence.'
    ]
  });

  function currentIdentity() {
    var section = document.querySelector('[data-nova-product-id]');
    var pathMatch = String(window.location && window.location.pathname || '')
      .match(/\/products\/([^/?#]+)/i);

    return {
      productId: clean(section && section.getAttribute('data-nova-product-id')),
      handle: key(
        section && section.getAttribute('data-nova-product-handle') ||
        pathMatch && pathMatch[1]
      ),
      vendor: clean(section && section.getAttribute('data-nova-product-vendor')),
      sku: clean(section && section.getAttribute('data-nova-product-sku-family'))
    };
  }

  function resolveStyle(identity) {
    identity = identity || currentIdentity();
    var productId = clean(identity.productId);
    var handle = key(identity.handle);
    var sku = skuKey(identity.sku);
    var match = null;

    STYLE_ORDER.some(function (styleId) {
      var palette = STYLE_PALETTES[styleId];
      if (productId && palette.productIds.indexOf(productId) !== -1) {
        match = palette;
        return true;
      }
      return false;
    });
    if (match) return match;

    STYLE_ORDER.some(function (styleId) {
      var palette = STYLE_PALETTES[styleId];
      if (sku && palette.skuFamilies.some(function (family) {
        return sku.indexOf(family) === 0;
      })) {
        match = palette;
        return true;
      }
      return false;
    });
    if (match) return match;

    STYLE_ORDER.some(function (styleId) {
      var palette = STYLE_PALETTES[styleId];
      if (handle && palette.handles.indexOf(handle) !== -1) {
        match = palette;
        return true;
      }
      return false;
    });
    return match;
  }

  function publicStyle(palette) {
    if (!palette) return null;
    return {
      id: palette.id,
      manufacturer: palette.manufacturer,
      style: palette.style,
      garmentFamily: palette.garmentFamily,
      source: palette.source,
      sourceUrl: palette.sourceUrl || '',
      notes: copy(palette.notes || [])
    };
  }

  function findColorRecord(palette, value) {
    if (!palette) return null;
    var raw = key(value);
    if (!raw) return null;
    /* Storefront color identity is exact. Status copy, row text, substring
       matches, and approximate names must never select a garment color. */
    return palette.colorLookup[raw] || null;
  }

  function resolveColor(options) {
    options = options || {};
    var input = clean(options.colorName);
    var identity = {
      productId: options.productId,
      handle: options.handle,
      vendor: options.vendor,
      sku: options.sku
    };
    var hasExplicitIdentity = Boolean(
      identity.productId || identity.handle || identity.vendor || identity.sku
    );
    var palette = options.styleId
      ? STYLE_PALETTES[key(options.styleId)]
      : resolveStyle(hasExplicitIdentity ? identity : currentIdentity());

    if (!palette) {
      return { supported: false, reason: 'unsupported-style', input: input };
    }

    var record = findColorRecord(palette, input);
    if (!record) {
      return {
        supported: false,
        reason: 'unsupported-color',
        input: input,
        styleId: palette.id
      };
    }

    var productId = clean(options.productId || currentIdentity().productId);
    var productOverrides = options.productOverrides || {};
    var overrideMap = productOverrides[productId] || {};
    var overrideHex = clean(
      overrideMap[record.colorName] || overrideMap[key(record.colorName)]
    ).toUpperCase();

    return {
      supported: true,
      styleId: palette.id,
      manufacturer: palette.manufacturer,
      style: palette.style,
      garmentFamily: palette.garmentFamily,
      colorName: record.colorName,
      canonicalKey: record.canonicalKey,
      hex: /^#[0-9A-F]{6}$/.test(overrideHex) ? overrideHex : record.hex,
      garmentSource: record.garmentSource,
      aliases: record.aliases.slice(),
      source: /^#[0-9A-F]{6}$/.test(overrideHex)
        ? 'Controlled product-specific override'
        : record.source,
      sourceUrl: record.sourceUrl,
      classification: /^#[0-9A-F]{6}$/.test(overrideHex)
        ? 'product-specific-digital-override'
        : record.classification
    };
  }

  function getColorMap(palette) {
    var map = {};
    if (!palette) return map;
    palette.colorOrder.forEach(function (colorName) {
      map[colorName] = palette.colorLookup[key(colorName)].hex;
    });
    return map;
  }

  function getColorManifest(palette) {
    if (!palette) return [];
    return palette.colorOrder.map(function (colorName) {
      var record = palette.colorLookup[key(colorName)];
      return {
        shopifyOptionValue: record.colorName,
        canonicalKey: record.canonicalKey,
        displayLabel: record.colorName,
        garmentSource: record.garmentSource,
        svgFill: record.hex,
        aliases: record.aliases.slice()
      };
    });
  }

  function setUnsupportedState(input, reason) {
    var shell = document.querySelector('[data-nova-proof-canvas-shell]');
    if (!shell) return;
    shell.setAttribute('data-nova-garment-color-supported', 'false');
    shell.setAttribute('data-nova-unsupported-garment-color', clean(input));
    var status = shell.querySelector('[data-nova-proof-preview-status]');
    if (status) status.textContent = 'Garment color preview unavailable';
    document.dispatchEvent(new CustomEvent('nova:unsupported-garment-color', {
      detail: { input: clean(input), reason: clean(reason) }
    }));
  }

  function clearUnsupportedState() {
    var shell = document.querySelector('[data-nova-proof-canvas-shell]');
    if (!shell) return;
    shell.setAttribute('data-nova-garment-color-supported', 'true');
    shell.removeAttribute('data-nova-unsupported-garment-color');
  }

  window.NovaApparelColorAPI = {
    version: '2.0.0-exact-manifest',
    registerStylePalette: registerStylePalette,
    getCurrentIdentity: function () { return copy(currentIdentity()); },
    getCurrentStyle: function () { return publicStyle(resolveStyle(currentIdentity())); },
    getStyle: function (styleId) { return publicStyle(STYLE_PALETTES[key(styleId)]); },
    getCurrentColorMap: function () { return getColorMap(resolveStyle(currentIdentity())); },
    getColorMap: function (styleId) { return getColorMap(STYLE_PALETTES[key(styleId)]); },
    getCurrentColorManifest: function () { return getColorManifest(resolveStyle(currentIdentity())); },
    getColorManifest: function (styleId) { return getColorManifest(STYLE_PALETTES[key(styleId)]); },
    getDefaultColorName: function () {
      var palette = resolveStyle(currentIdentity());
      return palette && palette.colorOrder[0] || '';
    },
    resolveColor: resolveColor,
    resolveCurrentColor: function (colorName) { return resolveColor({ colorName: colorName }); },
    normalizeCurrentColor: function (colorName) {
      var resolved = resolveColor({ colorName: colorName });
      return resolved.supported ? resolved.colorName : '';
    },
    setUnsupportedState: setUnsupportedState,
    clearUnsupportedState: clearUnsupportedState
  };
})();
